/**
 * Shop / theme helpers for embedded admin (App Store 1.1.1 + 5.1.3).
 * Prefer URL query + sessionStorage. Never require localStorage for auth or deep links.
 */

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>;
    };
  }
}

export function isEmbeddedAdmin(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('host') || p.get('embedded') === '1' || p.get('id_token')) {
      try {
        sessionStorage.setItem('gf_embedded', '1');
      } catch {
        /* ignore */
      }
      return true;
    }
    if (sessionStorage.getItem('gf_embedded') === '1') return true;
    // App Bridge global only exists inside Shopify admin iframe
    if (
      typeof window !== 'undefined' &&
      typeof window.shopify?.idToken === 'function'
    ) {
      return true;
    }
    if (window.self !== window.top) return true;
  } catch {
    return true; // cross-origin iframe → embedded
  }
  return false;
}

/** True when Vite/prod build is production mode */
export function isProdBuild(): boolean {
  return import.meta.env.PROD === true;
}

/**
 * Resolve myshopify.com domain for API headers and deep links.
 * Order: URL → sessionStorage → (dev only) localStorage → empty string.
 */
export function getShopDomain(): string {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('shop');
  if (fromUrl) {
    try {
      sessionStorage.setItem('gf_shop', fromUrl);
    } catch {
      /* ignore */
    }
    return fromUrl;
  }
  try {
    const fromSession = sessionStorage.getItem('gf_shop');
    if (fromSession) return fromSession;
  } catch {
    /* ignore */
  }
  // localStorage only outside production embedded (local dev)
  if (!isProdBuild() || !isEmbeddedAdmin()) {
    try {
      return localStorage.getItem('gf_shop') || '';
    } catch {
      return '';
    }
  }
  return '';
}

/**
 * Theme editor deep link to activate Freebi app embed.
 * Prefer API-provided deepLink; never build a URL with empty shop.
 */
export function themeEmbedDeepLink(
  preferred: string | undefined | null,
  shopDomain?: string | null,
): string | null {
  if (preferred && preferred !== '#') return preferred;

  const shop = (shopDomain || getShopDomain() || '').trim();
  const apiKey = (import.meta.env.VITE_SHOPIFY_API_KEY || '').trim();
  if (!shop || !apiKey) return null;

  // Prefer modern admin.shopify.com deep link (works inside Admin)
  const handle = shop.replace(/\.myshopify\.com$/i, '');
  return (
    `https://admin.shopify.com/store/${handle}/themes/current/editor` +
    `?context=apps&template=index&activateAppId=${apiKey}/app-embed`
  );
}
